/* ATTAR smooth scroll (2026-10-12)
   The page glides on the mouse wheel and trackpad instead of jumping in steps, and eases to a stop.
   Powered by Lenis 1.3.26 (assets/lenis.min.js, MIT). It moves the real page scroll, so the chapters,
   parallax photographs, reveals and sticky header keep working exactly as before.
   - Phones and tablets keep their own native touch scrolling (already smooth).
   - Off for visitors who ask their device for reduced motion, and inside the Theme Editor.
   - Pauses while a menu, drawer, search or cart locks the page; scrolls inside them stay native.
   - The theme's own smooth jumps (chapter links, back to top) use the same glide.
   Feel: change LERP below (0.06 = longer, heavier glide; 0.12 = quicker, lighter).
   To undo: remove the render 'attar-smooth-scroll' line from layout/theme.liquid. */
(() => {
  const LERP = 0.085;          // how quickly the page catches up with the wheel (lower = silkier, longer glide)
  const WHEEL = 0.95;          // distance per wheel notch
  const JUMP_SECONDS = 1.35;   // chapter links and back-to-top

  if (!window.Lenis) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.Shopify && Shopify.designMode) return;

  const html = document.documentElement;
  const easeOutQuart = t => 1 - Math.pow(1 - t, 4);

  const lenis = new Lenis({
    lerp: LERP,
    wheelMultiplier: WHEEL,
    smoothWheel: true,
    syncTouch: false,            // touch screens: native scrolling
    autoRaf: true,
    anchors: false,              // the theme already handles # links (attar-chapters.js)
    allowNestedScroll: false,    // see innerScroller: the theme's <body> has its own scroll box, which Lenis would mistake for a nested one
    stopInertiaOnNavigate: true,
    prevent: node => innerScroller(node) || !!(node.closest && node.closest(
      '.axe-drawer, .axe-search, #axh-menu, cart-drawer, .drawer, .modal, [role="dialog"], [data-lenis-prevent], iframe'
    ))
  });

  /* Lists, drawers and panels with their own vertical scrollbar keep native scrolling (not the page's <body>). */
  function innerScroller(el) {
    if (el === document.body || el === html) return false;
    const oy = getComputedStyle(el).overflowY;
    return (oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 2;
  }
  window.attarLenis = lenis;

  /* The theme's own scrolls: smooth ones use the glide, instant ones jump and keep Lenis in step. */
  const nativeTo = window.scrollTo.bind(window);
  const nativeBy = window.scrollBy.bind(window);
  const toY = (a, b) => (a && typeof a === 'object') ? (a.top ?? scrollY) : (b ?? scrollY);
  const isSmooth = a => a && typeof a === 'object' && a.behavior === 'smooth';
  const isLenisFrame = a => a && typeof a === 'object' && a.behavior === 'instant';   // Lenis moves the page this way
  window.scrollTo = window.scroll = function (a, b) {
    if (isLenisFrame(a)) return nativeTo(a);
    if (isSmooth(a)) return lenis.scrollTo(toY(a, b), { duration: JUMP_SECONDS, easing: easeOutQuart, force: true });
    nativeTo(a, b);
    lenis.scrollTo(toY(a, b), { immediate: true, force: true });
  };
  window.scrollBy = function (a, b) {
    const dy = (a && typeof a === 'object') ? (a.top || 0) : (b || 0);
    if (isSmooth(a)) return lenis.scrollTo(lenis.targetScroll + dy, { duration: JUMP_SECONDS, easing: easeOutQuart, force: true });
    nativeBy(a, b);
    lenis.scrollTo(scrollY, { immediate: true, force: true });
  };

  /* Pause while a menu, drawer or modal locks the page. */
  const locked = () => [html, document.body].some(el => el && getComputedStyle(el).overflowY === 'hidden');
  let wasLocked = false;
  const sync = () => {
    const l = locked();
    if (l === wasLocked) return;
    wasLocked = l;
    l ? lenis.stop() : lenis.start();
  };
  const mo = new MutationObserver(sync);
  const watch = el => el && mo.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });
  watch(html);
  if (document.body) watch(document.body); else document.addEventListener('DOMContentLoaded', () => { watch(document.body); sync(); });
  sync();

  /* Coming back to the tab or after the page grows (images, sections loading): keep in step. */
  addEventListener('load', () => lenis.resize());
  document.addEventListener('visibilitychange', () => { if (!document.hidden) lenis.resize(); });
})();
